import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { shopsAPI, teamsAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Wrench,
  Plus,
  Search,
  MapPin,
  Phone,
  Globe,
  Star,
  Edit,
  Trash2,
  Share2,
  Users,
  Navigation,
  ExternalLink,
} from "lucide-react";

const INITIAL_FORM = {
  name: "",
  address: "",
  phone: "",
  website: "",
  rating: "",
  notes: "",
  specialties: [],
};

export default function ShopFinder() {
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [myShops, setMyShops] = useState([]);
  const [teamShops, setTeamShops] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [teams, setTeams] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState("all");
  
  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  
  // Share modal
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharingShop, setSharingShop] = useState(null);

  useEffect(() => {
    loadInitialData();
    getUserLocation();
  }, []);

  useEffect(() => {
    loadShops();
  }, [selectedTeam]);

  const loadInitialData = async () => {
    try {
      const [teamsRes, specialtiesRes] = await Promise.all([
        teamsAPI.list(),
        shopsAPI.specialties(),
      ]);
      setTeams(teamsRes.data.teams || []);
      setSpecialties(specialtiesRes.data || []);
      await loadShops();
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadShops = async () => {
    try {
      const teamId = selectedTeam !== "all" ? selectedTeam : null;
      const { data } = await shopsAPI.list(teamId);
      setMyShops(data.my_shops || []);
      setTeamShops(data.team_shops || []);
    } catch (error) {
      toast.error("Failed to load shops");
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          // Location denied or unavailable
        }
      );
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const { data } = await shopsAPI.search(
        searchQuery,
        userLocation?.lat,
        userLocation?.lng
      );
      setSearchResults(data.results || []);
      if (data.results?.length === 0) {
        toast.info("No shops found. Try a different search.");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Google Places API not configured");
        setSearchResults([]);
      } else {
        toast.error("Search failed");
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSaveFromSearch = async (result) => {
    setSaving(true);
    try {
      await shopsAPI.create({
        name: result.name,
        address: result.address,
        place_id: result.place_id,
        latitude: result.latitude,
        longitude: result.longitude,
        rating: result.rating ? Math.round(result.rating) : null,
      });
      toast.success("Shop saved!");
      loadShops();
    } catch (error) {
      toast.error("Failed to save shop");
    } finally {
      setSaving(false);
    }
  };

  const openAddModal = () => {
    setEditingShop(null);
    setForm(INITIAL_FORM);
    setModalOpen(true);
  };

  const openEditModal = (shop) => {
    setEditingShop(shop);
    setForm({
      name: shop.name || "",
      address: shop.address || "",
      phone: shop.phone || "",
      website: shop.website || "",
      rating: shop.rating?.toString() || "",
      notes: shop.notes || "",
      specialties: shop.specialties || [],
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Shop name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        address: form.address || null,
        phone: form.phone || null,
        website: form.website || null,
        rating: form.rating ? parseInt(form.rating) : null,
        notes: form.notes || null,
        specialties: form.specialties,
      };

      if (editingShop) {
        await shopsAPI.update(editingShop.id, payload);
        toast.success("Shop updated");
      } else {
        await shopsAPI.create(payload);
        toast.success("Shop added");
      }

      setModalOpen(false);
      loadShops();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save shop");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (shopId) => {
    if (!confirm("Delete this shop?")) return;

    try {
      await shopsAPI.delete(shopId);
      toast.success("Shop deleted");
      loadShops();
    } catch (error) {
      toast.error("Failed to delete shop");
    }
  };

  const openShareModal = (shop) => {
    setSharingShop(shop);
    setShareModalOpen(true);
  };

  const handleShare = async (teamId) => {
    if (!sharingShop) return;

    try {
      const isShared = sharingShop.shared_with_teams?.includes(teamId);
      if (isShared) {
        await shopsAPI.unshare(sharingShop.id, teamId);
        toast.success("Shop unshared from team");
      } else {
        await shopsAPI.share(sharingShop.id, teamId);
        toast.success("Shop shared with team");
      }
      loadShops();
      // Refresh sharing shop data
      const { data } = await shopsAPI.get(sharingShop.id);
      setSharingShop(data);
    } catch (error) {
      toast.error("Failed to update sharing");
    }
  };

  const toggleSpecialty = (specialty) => {
    setForm((prev) => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter((s) => s !== specialty)
        : [...prev.specialties, specialty],
    }));
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? "text-amber-500 fill-amber-500" : "text-zinc-600"
            }`}
          />
        ))}
      </div>
    );
  };

  const ShopCard = ({ shop, isTeamShop = false }) => (
    <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-foreground">{shop.name}</h3>
              {isTeamShop && (
                <span className="text-xs bg-blue-500/20 text-blue-500 px-2 py-0.5 rounded-sm">
                  Team
                </span>
              )}
            </div>
            {shop.address && (
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{shop.address}</span>
              </p>
            )}
            <div className="flex items-center gap-4 mt-2 flex-wrap">
              {shop.phone && (
                <a href={`tel:${shop.phone}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {shop.phone}
                </a>
              )}
              {shop.website && (
                <a href={shop.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  Website
                </a>
              )}
            </div>
            {shop.specialties?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {shop.specialties.map((s) => (
                  <span key={s} className="text-xs bg-zinc-800 px-2 py-0.5 rounded-sm">
                    {s}
                  </span>
                ))}
              </div>
            )}
            {shop.notes && (
              <p className="text-sm text-muted-foreground mt-2 italic">
                "{shop.notes}"
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {renderStars(shop.rating)}
            {!isTeamShop && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => openShareModal(shop)}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => openEditModal(shop)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-400"
                  onClick={() => handleDelete(shop.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
            {shop.latitude && shop.longitude && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${shop.latitude},${shop.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="sm" className="border-zinc-700 text-xs">
                  <Navigation className="w-3 h-3 mr-1" />
                  Directions
                </Button>
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="back-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />

                <h1 className="text-lg font-bold text-foreground">Shop Finder</h1>
              </div>
            </div>
            <Button
              onClick={openAddModal}
              data-testid="add-shop-btn"
              className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Shop
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Search */}
        <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for shops (e.g., '4x4 repair near Denver')"
                  className="pl-10 bg-zinc-900 border-zinc-800"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="submit"
                disabled={searching}
                className="btn-tactical bg-primary text-primary-foreground"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
              </Button>
            </form>
            {userLocation && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Using your location for nearby results
              </p>
            )}
          </CardContent>
        </Card>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-foreground">Search Results</h2>
            {searchResults.map((result) => (
              <Card key={result.place_id} className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-foreground">{result.name}</h3>
                      {result.address && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {result.address}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        {result.rating && renderStars(Math.round(result.rating))}
                        {result.open_now !== undefined && (
                          <span className={`text-xs ${result.open_now ? "text-green-500" : "text-red-500"}`}>
                            {result.open_now ? "Open Now" : "Closed"}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleSaveFromSearch(result)}
                      disabled={saving}
                      variant="outline"
                      size="sm"
                      className="border-zinc-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filter */}
        {teams.length > 0 && (
          <div className="flex items-center gap-4">
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800">
                <Users className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Filter by team" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                <SelectItem value="all">All Shops</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* My Shops */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-foreground">My Saved Shops ({myShops.length})</h2>
          {myShops.length === 0 ? (
            <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
              <CardContent className="p-8 text-center">
                <Wrench className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-muted-foreground">No saved shops yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Search for shops or add one manually
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myShops.map((shop) => (
                <ShopCard key={shop.id} shop={shop} />
              ))}
            </div>
          )}
        </div>

        {/* Team Shops */}
        {teamShops.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-foreground">Team Shared Shops ({teamShops.length})</h2>
            <div className="space-y-3">
              {teamShops.map((shop) => (
                <ShopCard key={shop.id} shop={shop} isTeamShop />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingShop ? "Edit Shop" : "Add Shop"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shop-name">Shop Name *</Label>
              <Input
                id="shop-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Trail Ready 4x4"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shop-address">Address</Label>
              <Input
                id="shop-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="123 Main St, City, State"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="grid gap-4 grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="shop-phone">Phone</Label>
                <Input
                  id="shop-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shop-rating">Rating (1-5)</Label>
                <Select value={form.rating} onValueChange={(v) => setForm({ ...form, rating: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="none">No rating</SelectItem>
                    {[1, 2, 3, 4, 5].map((r) => (
                      <SelectItem key={r} value={r.toString()}>{r} Star{r > 1 ? "s" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shop-website">Website</Label>
              <Input
                id="shop-website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://example.com"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="space-y-2">
              <Label>Specialties</Label>
              <div className="flex flex-wrap gap-2">
                {specialties.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSpecialty(s)}
                    className={`text-xs px-2 py-1 rounded-sm border transition-colors ${
                      form.specialties.includes(s)
                        ? "bg-primary/20 text-primary border-primary/50"
                        : "bg-zinc-900 text-muted-foreground border-zinc-800 hover:border-zinc-700"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shop-notes">Notes</Label>
              <Textarea
                id="shop-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Your experience, recommendations, etc."
                className="bg-zinc-900 border-zinc-800"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-tactical bg-primary text-primary-foreground"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingShop ? "Update" : "Add Shop"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Share with Teams</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {teams.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                You're not a member of any teams yet
              </p>
            ) : (
              teams.map((team) => {
                const isShared = sharingShop?.shared_with_teams?.includes(team.id);
                return (
                  <div
                    key={team.id}
                    className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-sm border border-zinc-800"
                  >
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">{team.name}</span>
                    </div>
                    <Button
                      variant={isShared ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleShare(team.id)}
                      className={isShared ? "bg-primary" : "border-zinc-700"}
                    >
                      {isShared ? "Shared" : "Share"}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
